<script setup lang="ts">
import type { UserCatalogDto } from '@/api/generator';
import type { DeepReadonly } from 'vue';
import { getDateTimeFormatted } from '@/components/formatters/utils.ts';
import VeeCheckbox from '@/components/forms/Checkbox/VeeCheckbox.vue';
import VeePasswordField from '@/components/forms/PasswordField/VeePasswordField.vue';
import Notification from '@/components/notifications/Notification.vue';
import type { NotificationMessage } from '@/components/notifications/types.ts';
import { useTranslation } from 'i18next-vue';
import { computed } from 'vue';

interface MaintenanceModeFieldsProps {
  // Field naming
  passwordFieldName?: string;
  ignoreLoggedInUsersFieldName?: string;

  // Exception data
  exceptionNotification?: NotificationMessage;
  notificationMessage?: NotificationMessage;
  loggedInUsers?: UserCatalogDto[] | DeepReadonly<UserCatalogDto[]>;

  // Events
  onPasswordEnter?: () => void;
}

const {
  passwordFieldName = 'password',
  ignoreLoggedInUsersFieldName = 'ignoreLoggedInUsers',
  exceptionNotification = undefined,
  notificationMessage = undefined,
  loggedInUsers = undefined,
  onPasswordEnter = undefined,
} = defineProps<MaintenanceModeFieldsProps>();

const { t } = useTranslation();

defineOptions({
  inheritAttrs: false,
});

const loggedInUserListItems = computed(() => {
  if (loggedInUsers) {
    return loggedInUsers.map((user) => ({
      prependAvatar: user.profilePictureId ? `/api/files/${user.profilePictureId}` : '',
      title: user.fullName,
      subtitle:
        getDateTimeFormatted({
          value: user.lastLoginTime,
        }) ?? undefined,
    }));
  }
  return [];
});

const showLoggedInUsersList = computed(() => loggedInUserListItems.value?.length);

function handlePasswordEnter() {
  if (onPasswordEnter) {
    onPasswordEnter();
  }
}
</script>

<template>
  <div v-bind="$attrs">
    <VeePasswordField
      :name="passwordFieldName"
      :label="t('Password')"
      @keydown.enter="handlePasswordEnter"
    />

    <!-- Notifications -->
    <Notification
      v-if="exceptionNotification || notificationMessage"
      class="mt-5"
      :value="exceptionNotification ?? notificationMessage"
    />

    <!-- Logged-in users list -->
    <template v-if="showLoggedInUsersList">
      <v-virtual-scroll class="mt-5" :items="loggedInUserListItems" height="200" :itemHeight="56">
        <template #default="{ item }">
          <v-list-item
            :prependAvatar="item.prependAvatar"
            :title="item.title"
            :subtitle="item.subtitle"
          />
        </template>
      </v-virtual-scroll>

      <VeeCheckbox :name="ignoreLoggedInUsersFieldName" :label="t('SignOutUsersAndProceed')" />
    </template>
  </div>
</template>
